// Generic "Name-Percent" split grammar, shared by access-method splits
// (`Swing-40;Chair-25;Robot-35`) and damage-combination splits (`C2A-60;C3B-40`).
// Mirrors backend/utils/percentSplits.js — keep the two in step.

const NAME_PERCENT_PATTERN = /^(.+)-\s*([0-9]+(?:\.[0-9]+)?)%?$/;

export const PERCENT_TOTAL_TOLERANCE = 0.01;

// Parse "Swing-40;Chair-60" (or a bare "Swing" meaning 100%) into rows.
export const parseSplitsValue = (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return [];

  const tokens = value
    .split(";")
    .map((token) => token.trim())
    .filter(Boolean);

  return tokens.map((token) => {
    const match = token.match(NAME_PERCENT_PATTERN);
    if (!match) {
      return { name: token, percentage: tokens.length === 1 ? 100 : "" };
    }
    return { name: match[1].trim(), percentage: parseFloat(match[2]) };
  });
};

export const buildSplitsValue = (rows) => {
  if (rows.length === 0) return "";
  if (
    rows.length === 1 &&
    Math.abs(Number(rows[0].percentage) - 100) <= PERCENT_TOTAL_TOLERANCE
  ) {
    return String(rows[0].name).trim();
  }
  return rows
    .map((row) => `${String(row.name).trim()}-${Number(row.percentage)}`)
    .join(";");
};

export const getElementArea = (el) => {
  const area = parseFloat(el?.totalArea ?? el?.total_area ?? 0);
  return Number.isFinite(area) && area > 0 ? area : 0;
};

// Turn a stored splits value + element area into [{name, percentage, area}].
export const computeSplitAreas = (rawValue, area) => {
  const safeArea = Number(area) || 0;
  return parseSplitsValue(rawValue)
    .filter((split) => split.name && Number(split.percentage) > 0)
    .map((split) => ({
      name: split.name,
      percentage: Number(split.percentage),
      area: (safeArea * Number(split.percentage)) / 100,
    }));
};

// Aggregate per-name areas across elements; area with no split is "unassigned".
export const summarizeSplitAreas = (elements, getValue) => {
  const areaByName = new Map();
  let unassignedArea = 0;

  (elements || []).forEach((el) => {
    const area = getElementArea(el);
    const splits = computeSplitAreas(getValue(el), area);
    if (splits.length === 0) {
      unassignedArea += area;
      return;
    }
    splits.forEach((split) => {
      areaByName.set(split.name, (areaByName.get(split.name) || 0) + split.area);
    });
  });

  return {
    entries: Array.from(areaByName.entries()).map(([name, area]) => ({
      name,
      area,
    })),
    unassignedArea,
  };
};

export const formatArea = (value) =>
  (Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// "Robot 44.80 m² · Swing 67.20 m²" for compact one-line displays.
export const formatSplitAreasInline = (rawValue, area) => {
  const splits = computeSplitAreas(rawValue, area);
  if (splits.length === 0) return "";
  if (!(Number(area) > 0)) {
    // No area to distribute; fall back to the raw splits value.
    return String(rawValue || "").trim();
  }
  return splits
    .map((split) => `${split.name} ${formatArea(split.area)} m²`)
    .join(" · ");
};
